import React, { useState } from 'react';
import { FileEdit, CheckCircle2, Circle, ChevronRight, Trash2, ExternalLink, Tag, CreditCard, Building2, Users, Settings, Shield, Database, ClipboardList } from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { STEP_LABELS } from './tenants/wizard/types';
import { calculateReadiness } from './tenants/wizard/readiness';
import type { OnboardingDraftRecord } from '../services/onboardingDraftService';

// ============================================================
// Types
// ============================================================

export type DraftPanelTab = 'summary' | 'progress';

export interface DraftPanelProps {
  draft: OnboardingDraftRecord;
  onResume: (id: string) => void;
  onDelete: (draft: OnboardingDraftRecord) => void;
}

// ============================================================
// Helpers
// ============================================================

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

const STEP_ICONS: any[] = [
  Building2,    // 1 Department Info
  Tag,          // 2 Organization & Tier
  CreditCard,   // 3 Billing
  ClipboardList,// 4 Inspection Plan
  Users,        // 5 Contacts
  Settings,     // 6 Modules & Settings
  Shield,       // 7 Admin & Auth
  Database,     // 8 Initial Data Setup
  CheckCircle2, // 9 Review & Create
];

function ReadField({ label, value, mono, textPrimary, textSecondary }: {
  label: string;
  value?: any;
  mono?: boolean;
  textPrimary?: string;
  textSecondary?: string;
}) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div>
      <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>{label}</dt>
      <dd className={`text-sm break-all ${mono ? 'font-mono text-xs' : ''}`} style={{ color: textPrimary }}>{display}</dd>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, textSecondary }: { icon: any; title: string; textSecondary?: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="h-3.5 w-3.5" style={{ color: textSecondary }} />
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>{title}</h4>
    </div>
  );
}

// ============================================================
// DraftPanel
// ============================================================

export default function DraftPanel({ draft, onResume, onDelete }: DraftPanelProps) {
  const { theme: t } = useTheme();
  const [activeTab, setActiveTab] = useState<DraftPanelTab>('summary');

  const ws = draft.wizardState;
  const info = ws?.departmentInfo ?? {};
  const addr = info.address ?? {};
  const billing = ws?.billing ?? {};
  const lastStep = draft.lastStep ?? 1;
  const completedSteps: number[] = ws?.completedSteps ?? [];

  let readinessScore = 0;
  try { readinessScore = calculateReadiness(ws ?? {}).score; } catch { /* ignore */ }

  const readinessColor = readinessScore === 100 ? 'bg-green-500' : readinessScore >= 60 ? 'bg-amber-400' : 'bg-red-400';

  const tabs: { id: DraftPanelTab; label: string }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'progress', label: 'Progress' },
  ];

  // ── Summary Tab ──

  const renderSummary = () => (
    <div className="space-y-4 p-4">
      {/* Readiness */}
      <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium" style={{ color: t.textSecondary }}>Readiness</span>
          <span className="text-xs font-semibold" style={{ color: t.textPrimary }}>{readinessScore}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${readinessColor}`} style={{ width: `${readinessScore}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs" style={{ color: t.textSecondary }}>Step {lastStep} of {STEP_LABELS.length}</span>
          <span className="text-xs" style={{ color: t.textSecondary }}>
            Last updated {draft.updatedAt ? new Date(draft.updatedAt).toLocaleDateString() : '—'}
          </span>
        </div>
      </div>

      {/* Department Info */}
      <div>
        <SectionHeader icon={Building2} title="Department Info" textSecondary={t.textSecondary} />
        <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <ReadField label="Department Type" value={info.departmentType} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            <ReadField label="NERIS ID" value={info.nerisId} mono textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            <ReadField label="Contact Email" value={info.contactEmail} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            <ReadField label="Timezone" value={info.timezone} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            {(addr.city || addr.state) && (
              <ReadField
                label="Location"
                value={[addr.city, addr.state].filter(Boolean).join(', ')}
                textPrimary={t.textPrimary} textSecondary={t.textSecondary}
              />
            )}
          </dl>
        </div>
      </div>

      {/* Billing / Tier */}
      <div>
        <SectionHeader icon={CreditCard} title="Billing & Tier" textSecondary={t.textSecondary} />
        <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <dt className="text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Tier</dt>
              <dd>
                {ws?.tier ? (
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[ws.tier] || 'bg-gray-100 text-gray-600'}`}>
                    {ws.tier}
                  </span>
                ) : <span style={{ color: t.textSecondary }}>—</span>}
              </dd>
            </div>
            <ReadField
              label="Billing Cycle"
              value={billing.billingCycle ? billing.billingCycle.charAt(0).toUpperCase() + billing.billingCycle.slice(1) : undefined}
              textPrimary={t.textPrimary} textSecondary={t.textSecondary}
            />
            <ReadField
              label="Subscription Type"
              value={billing.subscriptionType}
              textPrimary={t.textPrimary} textSecondary={t.textSecondary}
            />
            <ReadField
              label="Start Date"
              value={billing.startDate ? new Date(billing.startDate).toLocaleDateString() : undefined}
              textPrimary={t.textPrimary} textSecondary={t.textSecondary}
            />
          </dl>
        </div>
      </div>

      {/* Resume CTA */}
      <button
        type="button"
        onClick={() => onResume(draft._id)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors"
      >
        <ExternalLink className="h-4 w-4" />
        Resume Draft
      </button>

      {/* Delete */}
      <button
        type="button"
        onClick={() => onDelete(draft)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
      >
        <Trash2 className="h-4 w-4" />
        Delete Draft
      </button>
    </div>
  );

  // ── Progress Tab ──

  const renderProgress = () => (
    <div className="p-4">
      <ul className="space-y-1">
        {STEP_LABELS.map((label, idx) => {
          const stepNum = idx + 1;
          const isCurrent = stepNum === lastStep;
          const isDone = completedSteps.includes(stepNum) || stepNum < lastStep;
          const StepIcon = STEP_ICONS[idx] ?? Circle;

          return (
            <li
              key={stepNum}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${isCurrent ? 'ring-1 ring-orange-300' : ''}`}
              style={{
                background: isCurrent ? t.accent + '12' : 'transparent',
              }}
            >
              {/* Status icon */}
              <div className="shrink-0">
                {isDone ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : isCurrent ? (
                  <ChevronRight className="h-4 w-4" style={{ color: t.accent }} />
                ) : (
                  <Circle className="h-4 w-4 text-gray-300" />
                )}
              </div>

              {/* Step icon */}
              <StepIcon
                className="h-3.5 w-3.5 shrink-0"
                style={{ color: isDone ? 'rgb(34 197 94)' : isCurrent ? t.accent : t.textSecondary }}
              />

              {/* Label */}
              <span
                className="text-sm flex-1"
                style={{ color: isDone ? t.textPrimary : isCurrent ? t.accent : t.textSecondary, fontWeight: isCurrent ? 600 : 400 }}
              >
                {label}
              </span>

              {/* Badge */}
              {isDone && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 shrink-0">Done</span>
              )}
              {isCurrent && (
                <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ background: t.accent + '20', color: t.accent }}>Current</span>
              )}
            </li>
          );
        })}
      </ul>

      {/* Resume CTA */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => onResume(draft._id)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors"
        >
          <FileEdit className="h-4 w-4" />
          Continue at Step {lastStep}
        </button>
      </div>
    </div>
  );

  // ── Render ──

  return (
    <div className="flex flex-col h-full" style={{ color: t.textPrimary }}>
      {/* Tab bar */}
      <div className="flex gap-0 border-b shrink-0" style={{ borderColor: t.accent + '30' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id ? 'border-orange-500' : 'border-transparent hover:border-gray-300'
            }`}
            style={{ color: activeTab === tab.id ? t.accent : t.textSecondary }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'summary' && renderSummary()}
        {activeTab === 'progress' && renderProgress()}
      </div>
    </div>
  );
}
